import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState } from "recoil";
// material
import { Alert, Box, Button, Container, Stack, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { authStateAtom } from "@states/auth";
import axios from "@utils/axios";
import { setSession } from "@utils/jwt";
import { PATH_AUTH } from "@routes/paths";
import { UserType } from "../../types/auth";
import IdentityForm from "./IdentityForm";
import { isServerTrouble } from "./failureMessage";
import { commitRememberedEmail } from "./rememberedEmail";
import { hasMagicToken, readMagicToken } from "./magicToken";
import { openSession } from "./session";

/**
 * 메일의 로그인 링크가 닿는 화면.
 *
 * ★ **여는 것만으로는 로그인되지 않는다.** 사용자가 버튼을 눌러야 서버에
 * `POST` 가 나간다.
 *
 * 왜 한 단계를 더 두나 — 아웃룩·지메일 같은 메일 보안 스캐너가 본문의 링크를
 * **사용자보다 먼저 열어 본다.** 링크를 여는 것만으로 로그인이 완료되는 구조라면
 * 1회용 토큰이 그때 타 버리고, 정작 사람이 누르면 «이미 쓴 링크» 가 된다.
 * 받는 환경에 따라 되는 사람과 안 되는 사람이 갈리는데 원인을 찾기 어렵다.
 * 스캐너는 버튼을 누르지 않으므로, 버튼 한 번이 그 문제를 통째로 없앤다.
 */
export default function MagicLink() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [authState, setAuthStates] = useRecoilState(authStateAtom);


  /**
   * 첫 렌더에서 **한 번만** 읽는다. 아래에서 주소를 바꾸므로 그 뒤에는 주소에
   * 토큰이 없다.
   *
   * ## 프래그먼트(`#token=`)를 먼저 본다
   *
   * 메일이 보내는 주소가 `#token=...` 으로 바뀌었다. `?token=...` 은 **서버로
   * 전송되어** ALB·CloudFront 액세스 로그와 `Referer` 에 평문으로 남는데, 비밀번호가
   * 없는 서비스에서 이 토큰은 **유일한 자격증명**이다. `#` 뒤는 브라우저가 서버로
   * 보내지 않는다.
   *
   * 쿼리스트링도 계속 읽는다 — 바꾸기 **전에 나간 메일**이 15분 동안 살아 있고,
   * 그 링크를 누른 사람이 «쓸 수 없는 링크» 를 보면 안 된다.
   */
  const [token] = useState(() =>
    readMagicToken(window.location.search, window.location.hash),
  );
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");

  /**
   * 본인인증이 남았다는 것을 서버가 알려 줬다.
   *
   * 서버가 실패 이유를 둘로 갈라 주기 때문에 알 수 있다 — `IDENTITY_REQUIRED` 는
   * «인증만 하면 된다»(링크는 살아 있다)이고, 그냥 400 은 «링크가 죽었다» 다.
   * 한 가지 답만 왔다면, 인증만 하면 들어올 수 있는 사람에게도 «링크를 다시
   * 받으세요» 라고 말하게 된다.
   */
  const [needIdentity, setNeedIdentity] = useState(false);

  /**
   * 그 인증이 **이번 한 번뿐인가.**
   *
   * 서버가 이유를 갈라 준다 — `IDENTITY_REQUIRED_ONCE` 는 «지문이 아직 없어서
   * 처음 한 번», 그냥 `IDENTITY_REQUIRED` 는 «관리자가 매번 받도록 켜 두었다» 다.
   *
   * 예상 못 한 단계를 만난 사람에게 이 한 문장이 크다. 「또 해야 하나」와
   * 「이번만 하면 되는구나」는 전혀 다른 일이다.
   */
  const [onlyOnce, setOnlyOnce] = useState(false);

  /**
   * ★ **들어올 때 이미 로그인해 있었으면 그냥 홈으로 보낸다.**
   *
   * 메일함에 남은 옛 링크를 나중에 다시 누르는 일은 흔하다. 그때 «이 링크는 사용할
   * 수 없습니다» 를 띄우면, **멀쩡히 들어와 있는 사람에게 쫓겨난 듯한 화면**을
   * 보여 주는 셈이다.
   *
   * 링크를 소비하지 않는다 — 살아 있으면 살아 있는 채로 둔다.
   *
   * ★★ **첫 렌더에서 한 번만 읽는다.**
   *
   * 인증 상태를 계속 지켜보면 **로그인에 성공한 순간에도 발동한다.** 로그인 성공이
   * 곧 «인증됨» 으로의 변화이기 때문이다. 그래서 링크를 눌러 정상적으로 들어온
   * 사람에게도 «이미 로그인되어 있습니다» 가 떴다. 여기서 보려는 것은 «들어올 때
   * 이미 로그인해 있었나» 이지 «지금 로그인됐나» 가 아니다.
   *
   * ★★★ 그런데 **조용히** 보내면 안 됐다(2026-09-23 공격 시험 f1-7). A 로 들어와 있는
   * 브라우저에서 B 의 링크(승인 메일 · 이메일 찾기 — 서버는 한 사람의 계정 전부에 보낸다)를
   * 누르면 A 의 홈이 뜨고 B 로 들어갔다고 믿는다. 공용 PC 에 남의 세션이 있으면 남의 계정
   * 안에서 자기 일을 한다. 오표시(«정상 로그인에도 뜬다»)의 원인은 첫 렌더 고정으로 이미
   * 사라졌으니, **누구로 들어와 있는지 말하고 고르게 한다.** 링크는 아직 쓰지 않았다.
   */
  const [wasLoggedInAs] = useState<string | null>(() =>
    authState.isInitialized && authState.isAuthenticated
      ? authState.user?.email ?? "다른 계정"
      : null,
  );
  /** 이미 로그인해 있던 사람이 «이 링크로 들어가겠다» 고 고른 뒤. */
  const [switching, setSwitching] = useState(false);

  const switchAccount = () => {
    // 지금 세션을 닫고 이 링크로 들어간다. 토큰은 이미 주소에서 읽어 들고 있다.
    setSession(null);
    setAuthStates({ isInitialized: true, isAuthenticated: false, permissions: [], user: null });
    setSwitching(true);
  };

  /**
   * 주소에서 토큰을 지운다. **이 값 하나로 계정에 들어올 수 있다.**
   * 그대로 두면 방문 기록에 남고, 사용자가 주소를 복사해 보내면 그대로 넘어간다.
   * 화면은 그대로 두고 주소만 바꾼다.
   */
  useEffect(() => {
    // 프래그먼트든 쿼리스트링이든 **주소에서 지운다.** 방문 기록에 남고,
    // 사용자가 주소를 복사해 보내면 그대로 넘어간다.
    if (hasMagicToken(window.location.search, window.location.hash)) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [params]);

  const signIn = async () => {
    setBusy(true);
    setFailed("");
    try {
      const answer = await axios({
        method: "post",
        url: "auth/magic/consume",
        data: { token },
      });
      const user: UserType = await openSession(answer.data.access_token);
      // «기억하기» 를 켜고 링크를 받았으면 **이제** 기억한다 — 로그인이 실제로 됐다(f1-6).
      commitRememberedEmail(user.email);
      setAuthStates({
        isInitialized: true,
        isAuthenticated: true,
        permissions: user.permissions ?? [],
        user,
      });
      navigate("/", { replace: true });
    } catch (error: any) {
      setBusy(false);
      if (
        error?.detail === "IDENTITY_REQUIRED" ||
        error?.detail === "IDENTITY_REQUIRED_ONCE"
      ) {
        setOnlyOnce(error.detail === "IDENTITY_REQUIRED_ONCE");
        setNeedIdentity(true);
        return;
      }
      // ★ 서버가 죽은 것과 링크가 죽은 것은 **사용자가 할 행동이 다르다.**
      // 전자는 기다리면 되고, 후자는 링크를 다시 받아야 한다. 둘을 같은 문구로
      // 뭉개면 고칠 수 없는 쪽으로 사람을 보낸다 — 실제로 개발에서 그랬다.
      // 응답을 아예 못 받은 것(연결 끊김 · CORS 없는 ALB 502/504)도 서버 쪽 문제다 —
      // «링크가 죽었다» 로 보내면 살아 있는 링크를 버리게 된다(f1-0 화면 쪽).
      if (isServerTrouble(error)) {
        // «링크는 살아 있습니다» 라고 **단정하지 않는다.** 토큰을 쓴 뒤에 터진
        // 500 이면 이미 소비된 상태다. 확실한 것만 말하고, 안 되면 다음 수를 준다.
        setFailed(
          "지금 서버에 문제가 있어 로그인하지 못했습니다. " +
            "잠시 뒤 다시 시도해주시고, 그래도 안 되면 링크를 다시 받아주세요.",
        );
        return;
      }
      // 서버는 «이미 썼다 · 만료됐다 · 없는 토큰이다» 를 구분해 주지 않는다.
      // 구분해 주면 남의 토큰을 넣어 보며 상태를 읽을 수 있기 때문이다.
      // 화면도 같은 이유로 한 가지 문구만 보여 준다.
      setFailed(
        "이 링크는 사용할 수 없습니다. 이미 사용했거나 15분이 지났거나, " +
          "그 뒤에 새 링크를 받으셨을 수 있습니다. " +
          "메일함에서 가장 마지막에 온 메일의 링크를 눌러주세요.",
      );
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 10 }}>
        <Stack spacing={3}>
          <Typography variant="h4">로그인</Typography>

          {wasLoggedInAs && !switching && (
            <>
              <Alert severity="info">
                이미 <b>{wasLoggedInAs}</b> 계정으로 로그인되어 있습니다.
                <br/>
                이 링크의 계정으로 들어가려면 아래에서 바꿔주세요. 이 링크는 아직 사용하지 않았습니다.
              </Alert>
              <Button fullWidth size="large" variant="contained" onClick={() => navigate("/", { replace: true })}>
                지금 계정으로 계속
              </Button>
              {token && (
                <Button fullWidth size="large" variant="outlined" onClick={switchAccount}>
                  로그아웃하고 이 링크로 로그인
                </Button>
              )}
            </>
          )}

          {(!wasLoggedInAs || switching) && !token && (
            <Alert severity="warning">
              로그인 링크가 올바르지 않습니다. 메일의 버튼을 다시 눌러주세요.
            </Alert>
          )}
          {failed && <Alert severity="error">{failed}</Alert>}

          {(!wasLoggedInAs || switching) && token && !failed && needIdentity && (
            /* 인증이 끝나면 **같은 버튼을 다시 누른 것처럼** 로그인을 이어서 한다.
               사용자가 «이제 뭘 해야 하지» 를 겪지 않게. */
            <IdentityForm token={token} onDone={signIn} onlyOnce={onlyOnce}/>
          )}

          {(!wasLoggedInAs || switching) && token && !failed && !needIdentity && (
            <>
              <Typography sx={{ color: "text.secondary" }}>
                아래 버튼을 누르면 로그인됩니다. 이 링크는 한 번만 사용할 수 있습니다.
              </Typography>
              <LoadingButton
                fullWidth
                size="large"
                variant="contained"
                loading={busy}
                onClick={signIn}
              >
                로그인하기
              </LoadingButton>
            </>
          )}

          {/* 본인인증 도중에도 나갈 길을 둔다. 링크 수명(15분)이 인증 중에 끝나거나 이름이 달라
              403 이 나면 IdentityForm 에 갇혔다 — 메일에서 새 탭으로 열려 뒤로가기도 머리글도
              없다(f1-5). */}
          {(!wasLoggedInAs || switching) && (failed || !token || needIdentity) && (
            <Button fullWidth size="large" onClick={() => navigate(PATH_AUTH.login)}>
              로그인 화면으로
            </Button>
          )}
        </Stack>
      </Box>
    </Container>
  );
}
